/**
 * Statistics Drawer for Mobile
 * Displays library statistics in an inline drawer
 */

import React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';

interface LibraryStats {
  totalDrills: number;
  positionalCount: number;
  weightRoomCount: number;
  rollingCount: number;
  phase1Count: number;
  phase2Count: number;
  phase3Count: number;
}

interface StatisticsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  stats: LibraryStats;
  getTagColor: (category: string, value: string, preferences: any) => string;
  tagPreferences: any;
}

export const StatisticsDrawer: React.FC<StatisticsDrawerProps> = ({
  isOpen,
  onClose,
  stats,
  getTagColor,
  tagPreferences,
}) => {
  const isMobile = useIsMobile();

  if (!isOpen) return null;

  return (
    <>
      {isMobile && (
        <div 
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
          onClick={onClose}
        />
      )}
      <div 
        className={`
          ${isMobile ? 'fixed bottom-0 left-0 right-0 z-50' : 'relative'}
          bg-card border-t ${isMobile ? 'rounded-t-lg shadow-2xl' : 'rounded-lg border'} 
          ${isMobile ? 'max-h-[90vh]' : ''} flex flex-col
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold">Library Statistics</h2>
            <p className="text-sm text-muted-foreground">
              Overview of your drill library
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="px-4 py-4 pb-6 space-y-4">
            {/* Total Drills */}
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">Total Drills</span>
                <span className="text-2xl font-bold">{stats.totalDrills}</span>
              </div>
            </div>

            {/* Domain Breakdown */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                By Domain
              </h3>
              <div className="space-y-2">
                <div className="rounded-lg border p-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Positional</span>
                  <span 
                    className="text-xl font-bold"
                    style={{ color: getTagColor('Domain', 'Positional', tagPreferences) }}
                  >
                    {stats.positionalCount}
                  </span>
                </div>
                <div className="rounded-lg border p-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Weightroom</span>
                  <span 
                    className="text-xl font-bold"
                    style={{ color: getTagColor('Domain', 'Weightroom', tagPreferences) }}
                  >
                    {stats.weightRoomCount}
                  </span>
                </div>
                <div className="rounded-lg border p-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Rolling</span>
                  <span 
                    className="text-xl font-bold"
                    style={{ color: getTagColor('Domain', 'Rolling', tagPreferences) }}
                  >
                    {stats.rollingCount}
                  </span>
                </div>
              </div>
            </div>

            {/* Phase Breakdown */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                By Phase
              </h3>
              <div className="space-y-2">
                <div className="rounded-lg border p-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Phase 1</span>
                  <span 
                    className="text-xl font-bold"
                    style={{ color: getTagColor('Phase', 'Phase 1', tagPreferences) }}
                  >
                    {stats.phase1Count}
                  </span>
                </div>
                <div className="rounded-lg border p-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Phase 2</span>
                  <span 
                    className="text-xl font-bold"
                    style={{ color: getTagColor('Phase', 'Phase 2', tagPreferences) }}
                  >
                    {stats.phase2Count}
                  </span>
                </div>
                <div className="rounded-lg border p-3 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Phase 3</span>
                  <span 
                    className="text-xl font-bold"
                    style={{ color: getTagColor('Phase', 'Phase 3', tagPreferences) }}
                  >
                    {stats.phase3Count}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </>
  );
};

